import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { compose, mapProps } from 'recompose';
import { withAnalytic } from '@skooldio/analytics-provider/dist/Analytics/hoc';
import styled from 'styled-components';
import { Redirect, useLocation } from 'react-router';

import { Text, Box, Flex, Icon, ICON_LIST } from 'Components/Base';
import ButtonOutlinedIcon from 'Components/ButtonOutlinedIcon';
import IndividualUnitExporter from 'Containers/IndividualUnitExporter';
import IndividualUnit from 'Containers/IndividualUnit';
import { getSchoolCode } from 'Domains/LESchoolData/Routing';
import IndividualUnitReportData from 'Domains/IndividualUnitReportData';
import { useAnalytics } from 'Libs/Analytics';
import { SYLLABUS_MODE } from 'Domains/syllabus';

const getUpdateTime = () => {
  return moment().format('DD/MM/YYYY HH:mm');
};

const IndividualUnitPageWrapper = styled(Box)`
  border-radius: 10px;
  flex: 1 0;
  display: grid;
  grid: auto auto 1fr / 100%;
  row-gap: 16px;
`;

const StudentInfo = ({ filtered, username, schoolCode, mode }) => {
  return (
    <IndividualUnitReportData value={filtered} schoolCode={schoolCode} username={username}>
      {({ loading, error, data, refetch }) => {
        if (loading) return <Box />;
        if (error) return <Box />;
        const report =
          mode === SYLLABUS_MODE.TRADITIONAL
            ? data?.foIndividualUnitReport
            : data?.foTKIndividualUnitReport;
        const firstName = report?.firstName ?? '';
        const lastName = report?.lastName ?? '';
        const displayName = firstName ? `${firstName} ${lastName}` : lastName;
        if (!displayName) return <Box />;
        return <Text.Title>{displayName}</Text.Title>;
      }}
    </IndividualUnitReportData>
  );
};

const useQueryString = () => {
  return new URLSearchParams(useLocation().search);
};

const IndividualUnitPage = ({ schoolCode, username, onLogAction, history, location, ...props }) => {
  const { send } = useAnalytics();
  const { t } = useTranslation('individualUnit');
  const [isFetchData, setIsFetchData] = useState(false);
  const [disabledFetch, setDisabledFetch] = useState(true);
  const [appliedFilter, setAppliedFilter] = useState({ filters: {}, rawFilter: {} });
  const queryString = useQueryString();

  const handleFilterApplied = (filters, rawFilter) => setAppliedFilter({ filters, rawFilter });

  const filterValue = {
    term: queryString.get('term') ?? null,
    grade: queryString.get('grade') ?? null,
    classNo: queryString.get('class') ?? null,
    subject: queryString.get('subj') ?? null,
    syllabus: queryString.get('course') ?? null,
    mode: queryString.get('mode') ?? SYLLABUS_MODE.TRADITIONAL,
  };

  if (
    !username ||
    !filterValue.term ||
    !filterValue?.grade ||
    !filterValue.classNo ||
    !filterValue?.subject ||
    !filterValue?.syllabus
  ) {
    return <Redirect to={`/report/${schoolCode}/course-unit${location?.search}`} />;
  }

  return (
    <IndividualUnitPageWrapper px={[0, null, 2, 7]} py={[0, null, 3, 5]} bg={'white'}>
      <Box>
        <Text.Title
          fontSize={4}
          color="primary"
          mb={5}
          onClick={() => history.push(`/report/${schoolCode}/course-unit${location?.search}`)}
          style={{ cursor: 'pointer' }}
        >
          <Flex alignItems="flex-end">
            <Icon mr={2} name={ICON_LIST.BACK} />
            {t('BACK')}
          </Flex>
        </Text.Title>
        <Flex alignItems={'center'} justifyContent={'space-between'}>
          <StudentInfo
            filtered={appliedFilter?.rawFilter}
            schoolCode={schoolCode}
            username={username}
            mode={filterValue.mode}
          />
          <Flex>
            <IndividualUnitExporter
              disabled={disabledFetch}
              appliedFilter={appliedFilter?.filters}
              schoolCode={schoolCode}
              classroomUsername={username}
              mode={filterValue.mode}
            />
            <ButtonOutlinedIcon
              textProp={{
                fontWeight: 'semibold',
              }}
              ml={2}
              lineHeight={'0.5'}
              height={'40px'}
              leftIcon={<Icon name={ICON_LIST.REFRESH} />}
              message={t('UPDATE')}
              disabled={disabledFetch}
              onClick={() => {
                onLogAction({
                  category: 'Fatch data',
                  action: `Click`,
                  label: `Click on individual unit page`,
                });
                send('update', { on: 'individual_unit_page' });
                setIsFetchData(true);
              }}
            />
          </Flex>
        </Flex>
        <Flex justifyContent={'flex-end'} height={'16px'}>
          <Text.Hint
            mt={1}
            color={'timeUpdate'}
            textAlign={'right'}
            display={disabledFetch ? 'none' : 'block'}
          >
            {t('UPDATE_DESCRIPTION_TIME')} {getUpdateTime()}
          </Text.Hint>
        </Flex>
      </Box>

      <IndividualUnit
        isFetchData={isFetchData}
        setIsFetchData={setIsFetchData}
        setDisabledFetch={setDisabledFetch}
        onFilterApplied={handleFilterApplied}
        schoolCode={schoolCode}
        username={username}
      />
    </IndividualUnitPageWrapper>
  );
};

export default compose(mapProps(getSchoolCode), withAnalytic)(IndividualUnitPage);
